// Hash-based SPA router
// see architecture.md#L580, L1141
// 路由定義在 src/pos/routes.js（可單元測試）
//
// Codex W1 重要 #2：mount 前必檢查 auth 就緒狀態。原本 guard 只看 role，
// 若 dataset.authed 尚未寫入（極早期載入）或已被清掉（sign-out 後），
// 受保護 route 的 connectedCallback 會先跑（fetch 打出去），雖然 auth-gate
// 接著會蓋掉畫面，但 API 已被呼叫 + 401 log 噪音 + 有機會讓未授權資料短暫流出。

import { matchRoute, DEFAULT_ROUTE } from '../pos/routes.js';

let _mountPoint = null;

export function initRouter() {
  window.addEventListener('hashchange', handleRoute);
  // Codex W1 重要 #2：auth 狀態變化也要重新 evaluate 當前 route，
  // 例如 sign-out 後若當前在 owner-only route 需立即 unmount。
  window.addEventListener('auth:changed', handleRoute);
  handleRoute();
}

export function handleRoute() {
  const hash = location.hash || DEFAULT_ROUTE;
  const route = matchRoute(hash);

  if (!route) {
    if (hash !== DEFAULT_ROUTE) location.hash = DEFAULT_ROUTE;
    return;
  }

  const authed = document.body.dataset.authed === 'true';
  const tenantId = document.body.dataset.tenantId || '';
  const role = document.body.dataset.role || '';

  // 公開路由（QR 顧客點餐）直接 mount，跳過所有 auth 檢查
  if (route.guard === 'public') {
    mount(route.component);
    return;
  }

  // 未登入 / 未決定：不 mount 受保護路由（auth-gate 會接手顯示 pos-login）
  // 不觸發 redirect，避免洗掉 magic link 帶回的 access_token / code
  if (!authed) {
    unmount();
    return;
  }

  // 已登入但尚未 provision（無 tenant_id）— 同樣讓 auth-gate 接管顯示 pos-tenant-setup
  if (!tenantId) {
    unmount();
    return;
  }

  // role guard
  if (route.guard?.startsWith('role:')) {
    const requiredRole = route.guard.slice(5);
    if (role !== requiredRole) {
      // 不彈 alert（Codex 指 alert 會打斷 UX）；靜默 redirect
      unmount();
      if (hash !== DEFAULT_ROUTE) location.hash = DEFAULT_ROUTE;
      return;
    }
  }

  mount(route.component);
}

function mount(tagName) {
  if (!_mountPoint) {
    _mountPoint = document.querySelector('pos-app');
  }
  if (!_mountPoint) return;

  // 若已掛相同 tag，不重建（避免 hashchange 上無意義 remount）
  const existing = _mountPoint.querySelector('[slot="route"]');
  if (existing && existing.tagName.toLowerCase() === tagName.toLowerCase()) return;

  if (existing) existing.remove();

  const el = document.createElement(tagName);
  el.setAttribute('slot', 'route');
  _mountPoint.appendChild(el);
}

function unmount() {
  if (!_mountPoint) _mountPoint = document.querySelector('pos-app');
  if (!_mountPoint) return;
  _mountPoint.querySelectorAll('[slot="route"]').forEach((el) => el.remove());
}
